// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Notifications
// Training-related alerts: expiring, expired, upcoming classes
// Computed on the fly from store data (no persistent storage)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Text,
  Group,
  Button,
  Stack,
  Badge,
  Loader,
  Center,
} from '../../components/gab/GabComponents';
import {
  IconBell,
  IconBellOff,
  IconChecks,
  IconCalendar,
  IconAlertCircle,
  IconExternalLink,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useBlackboardStore } from '../../store/blackboardStore';
import { useAuthStore } from '../../store/authStore';
import dayjs from 'dayjs';

import type { BBUser } from '../../store/blackboardStore';

// ── Types ────────────────────────────────────────────────────

interface NotificationItem {
  id: string;
  type: 'training' | 'class';
  severity: 'expired' | 'urgent' | 'warning' | 'notice' | 'info';
  title: string;
  subtitle: string;
  dateLabel: string;
  sortDate: dayjs.Dayjs;
  sortPriority: number;
  trainingId?: string;
  read: boolean;
}

// ── Constants ────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<string, {
  color: string;
  label: string;
  badgeColor: string;
  priority: number;
}> = {
  expired: { color: '#dc2626', label: 'EXPIRED', badgeColor: 'red', priority: 0 },
  urgent: { color: '#ea580c', label: 'URGENT', badgeColor: 'orange', priority: 1 },
  warning: { color: '#d97706', label: 'WARNING', badgeColor: 'yellow', priority: 2 },
  notice: { color: '#4a82c4', label: 'NOTICE', badgeColor: 'blue', priority: 3 },
  info: { color: '#627d98', label: 'INFO', badgeColor: 'gray', priority: 4 },
};

// ── Notification Card ────────────────────────────────────────

function NotificationCard({ notification, onViewTraining }: { notification: NotificationItem; onViewTraining?: (trainingId: string) => void }) {
  const cfg = SEVERITY_CONFIG[notification.severity] || SEVERITY_CONFIG.info;
  const isClass = notification.type === 'class';
  const isRead = notification.read;

  return (
    <Paper
      p="md"
      style={{
        borderLeft: `3px solid ${cfg.color}`,
        opacity: isRead ? 0.7 : 1,
        transition: 'transform 150ms ease, box-shadow 150ms ease',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
        (e.currentTarget as HTMLElement).style.boxShadow =
          '0 4px 12px rgba(17,45,78,.06), 0 12px 36px rgba(17,45,78,.08)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLElement).style.boxShadow = '';
      }}
    >
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Box
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: `${cfg.color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isClass ? (
            <IconCalendar size={14} color={cfg.color} />
          ) : (
            <IconAlertCircle size={14} color={cfg.color} />
          )}
        </Box>

        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" align="flex-start" mb={4}>
            <Text size="sm" fw={600} style={{ flex: 1 }}>
              {notification.title}
            </Text>
            <Badge color={cfg.badgeColor} size="xs">
              {cfg.label}
            </Badge>
          </Group>

          <Text size="xs" c="dimmed" mb={6}>
            {notification.subtitle}
          </Text>

          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              {notification.dateLabel}
            </Text>
            {notification.type === 'training' && notification.trainingId && (
              <Button
                variant="subtle"
                size="xs"
                color="blue"
                leftSection={<IconExternalLink size={10} />}
                onClick={() => onViewTraining?.(notification.trainingId!)}
              >
                View Training
              </Button>
            )}
          </Group>
        </Box>
      </Group>
    </Paper>
  );
}

// ── Main Component ───────────────────────────────────────────

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    users,
    trainingRecords,
    classrooms,
    loadUsers,
    loadTrainingRecords,
    loadClassrooms,
  } = useBlackboardStore();

  const [initialLoad, setInitialLoad] = useState(true);
  const [allRead, setAllRead] = useState(false);

  // Determine current user's info from the users table
  const currentBBUser: BBUser | undefined = useMemo(() => {
    if (!user?.email) return undefined;
    return users.find(u => u.email.toLowerCase() === user.email.toLowerCase());
  }, [users, user]);

  const uid = currentBBUser ? String(currentBBUser.id) : '';
  const role = currentBBUser?.role || 'student';

  useEffect(() => {
    async function load() {
      await Promise.all([loadUsers(), loadTrainingRecords(), loadClassrooms()]);
      setInitialLoad(false);
    }
    load();
  }, [loadUsers, loadTrainingRecords, loadClassrooms]);

  // ── Build notifications ────────────────────────────────────

  const notificationItems = useMemo(() => {
    const items: NotificationItem[] = [];
    const now = dayjs();

    // 1. Training expiration notifications (user's own records)
    const ownRecords = uid ? trainingRecords.filter(r => r.userId === uid) : [];

    ownRecords.forEach(record => {
      if (!record.expiresDate) return;
      const expires = dayjs(record.expiresDate);
      const daysLeft = expires.diff(now, 'day');
      const trainingName = record.trainingName || record.trainingId || 'Training';

      let severity: NotificationItem['severity'] | null = null;
      let title = '';
      let subtitle = '';

      if (daysLeft < 0) {
        severity = 'expired';
        title = `${trainingName} - Expired`;
        subtitle = `This training expired ${Math.abs(daysLeft)} day(s) ago on ${expires.format('MMM D, YYYY')}. Please renew as soon as possible.`;
      } else if (daysLeft <= 7) {
        severity = 'urgent';
        title = `${trainingName} - Expiring in ${daysLeft} day(s)`;
        subtitle = `This training expires on ${expires.format('MMM D, YYYY')}. Immediate action required.`;
      } else if (daysLeft <= 30) {
        severity = 'warning';
        title = `${trainingName} - Expiring in ${daysLeft} days`;
        subtitle = `This training expires on ${expires.format('MMM D, YYYY')}. Schedule renewal soon.`;
      } else if (daysLeft <= 60) {
        severity = 'notice';
        title = `${trainingName} - Expiring in ${daysLeft} days`;
        subtitle = `This training expires on ${expires.format('MMM D, YYYY')}. Consider planning your renewal.`;
      }

      if (severity) {
        items.push({
          id: `record-${record.id}`,
          type: 'training',
          severity,
          title,
          subtitle,
          dateLabel: `Expires: ${expires.format('MMM D, YYYY')}`,
          sortDate: expires,
          sortPriority: SEVERITY_CONFIG[severity].priority,
          trainingId: record.trainingId,
          read: allRead,
        });
      }
    });

    // 2. Upcoming class notifications
    classrooms.forEach(cls => {
      if (cls.status === 'cancelled' || cls.status === 'completed' || cls.status === 'draft') return;

      // Check if current user is registered
      let isRegistered = false;
      try {
        const registered = JSON.parse(cls.registeredUsers || '[]');
        isRegistered = Array.isArray(registered) && registered.some(
          (r: { userId?: string }) => r.userId === uid,
        );
      } catch {
        isRegistered = false;
      }
      if (!isRegistered) return;

      if (!cls.classDate) return;
      const classDayjs = dayjs(cls.classDate);
      if (classDayjs.isBefore(now, 'day')) return;

      const daysUntil = classDayjs.diff(now, 'day');
      if (daysUntil > 14) return;

      const severity: NotificationItem['severity'] = daysUntil <= 1 ? 'urgent' : daysUntil <= 3 ? 'warning' : 'notice';

      items.push({
        id: `class-${cls.id}`,
        type: 'class',
        severity,
        title: `${cls.title || 'Class'} - ${classDayjs.format('MMM D')}`,
        subtitle: `${cls.location || 'Virtual'}${cls.classTime ? ` | ${cls.classTime}` : ''} | Instructor: ${cls.instructorName || '\u2014'}`,
        dateLabel: classDayjs.format('MMM D, YYYY'),
        sortDate: classDayjs,
        sortPriority: daysUntil <= 1 ? 1 : daysUntil <= 3 ? 2 : 3,
        read: allRead,
      });
    });

    // 3. Admin: org-wide expired training notifications
    if (role === 'admin') {
      const userMap: Record<string, string> = {};
      users.forEach(u => {
        userMap[String(u.id)] =
          `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
      });

      trainingRecords.forEach(record => {
        if (record.userId === uid) return; // Skip own records
        if (!record.expiresDate) return;

        const expires = dayjs(record.expiresDate);
        const daysLeft = expires.diff(now, 'day');
        if (daysLeft >= 0) return; // Admin only sees expired for other users

        const trainingName = record.trainingName || record.trainingId || 'Training';
        const userName = record.userName || userMap[record.userId] || 'Unknown User';

        items.push({
          id: `admin-${record.id}`,
          type: 'training',
          severity: 'expired',
          title: `${userName} - ${trainingName} Expired`,
          subtitle: `Expired ${Math.abs(daysLeft)} day(s) ago on ${expires.format('MMM D, YYYY')}.`,
          dateLabel: `Expired: ${expires.format('MMM D, YYYY')}`,
          sortDate: expires,
          sortPriority: 0,
          trainingId: record.trainingId,
          read: allRead,
        });
      });
    }

    // Sort: expired first, then by urgency, then by date
    items.sort((a, b) => {
      if (a.sortPriority !== b.sortPriority) return a.sortPriority - b.sortPriority;
      return a.sortDate.unix() - b.sortDate.unix();
    });

    return items;
  }, [trainingRecords, classrooms, users, uid, role, allRead]);

  const handleMarkAllRead = () => {
    setAllRead(true);
  };

  const handleViewTraining = useCallback((trainingId: string) => {
    navigate('/blackboard/records', { state: { filterTrainingId: trainingId } });
  }, [navigate]);

  // ── Render ─────────────────────────────────────────────────

  if (initialLoad) {
    return (
      <Center h={400}>
        <Loader color="blue" />
      </Center>
    );
  }

  const unreadCount = notificationItems.filter(n => !n.read).length;

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Text
            size="10px"
            fw={700}
            c="dimmed"
            style={{ textTransform: 'uppercase', letterSpacing: 2 }}
          >
            Alerts
          </Text>
          <Group gap="sm" align="center">
            <IconBell size={18} color="#1a365d" />
            <Text size="xl" fw={700} c="#1a365d">
              Notifications
            </Text>
            {unreadCount > 0 && (
              <Badge color="red" size="sm" variant="filled">
                {unreadCount}
              </Badge>
            )}
          </Group>
        </Box>
        {notificationItems.length > 0 && !allRead && (
          <Button
            variant="outline"
            color="blue"
            leftSection={<IconChecks size={13} />}
            onClick={handleMarkAllRead}
          >
            Mark All Read
          </Button>
        )}
      </Group>

      {/* Notification list */}
      {notificationItems.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="md">
            <Box
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#f1f3f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconBellOff size={28} color="#868e96" />
            </Box>
            <Text fw={600} size="lg" c="#334e68">
              No notifications
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              You are all caught up. No training expirations or upcoming classes require your
              attention.
            </Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="sm">
          {notificationItems.map(n => (
            <NotificationCard key={n.id} notification={n} onViewTraining={handleViewTraining} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
